import { rawText } from '@api/inteceptors/capitalize.interceptor';
import {
  DEFAULT_LIST_INITIAL_STATE,
  DEFAULT_LIST_STORAGE,
  LIST_TABLE_APPEARANCE,
} from '@app/common/listTableAppearance';
import useGlobalInventoryCategories from '@hooks/fetching/useGlobalInventoryCategories';
import useSmartErrorHandling from '@hooks/useSmartErrorHandling';
import { useConsumeSearchParam } from '@hooks/useConsumeSearchParam';
import { useEditorLoad } from '@hooks/useEditorLoad';
import { APP_PREFIX, INVENTORY_CATEGORY_PAGE } from '@app/routes/urls';
import { AppDataTable } from '@app/components/common/AppDataTable';
import { useLastListParams } from '@app/features/exports/useLastListParams';
import { App, Button, Drawer } from 'antd';
import React, { useCallback, useMemo, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';

import { createItem, deleteItem, getItem, listItems, updateItem } from '../../api';
import { InventoryForm } from '../InventoryForm/InventoryForm';
import InventoryMovementsSubTable, {
  InventoryReloadContext,
} from '../InventoryMovementsSubTable/InventoryMovementsSubTable';
import {
  getInventoryItemsTableColumns,
  type InventoryTableHandlers,
} from '../InventoryTable/inventoryTableColumns.config';
import RecordMovementModal from '../../RecordMovementModal';
import type { InventoryItem, InventoryItemRow } from '../../types';
import { useInventoryMoneyVisibility, type InventoryMoneyVisibility } from '../../moneyVisibility';

/** Reduce the full form payload to the writable subset shared by create & update; money the user is not shown is left out. */
const toWritableItem = (data: InventoryItem, { showCost, showPrices }: InventoryMoneyVisibility) => ({
  name: data.name,
  code: data.code ?? null,
  unit: data.unit,
  categoryId: data.categoryId ?? null,
  description: data.description ?? null,
  isConsumable: Boolean(data.isConsumable),
  isSellable: Boolean(data.isSellable),
  isRentable: Boolean(data.isRentable),
  reorderThreshold: data.reorderThreshold ?? null,
  ...(showCost && { defaultCost: data.defaultCost ?? null }),
  ...(showPrices && {
    defaultSalePrice: data.defaultSalePrice ?? null,
    defaultRentalRate: data.defaultRentalRate ?? null,
    defaultRentalRateUnit: data.defaultRentalRateUnit ?? null,
  }),
});

export const NOT_EXPORTED_INVENTORY_ITEMS = ['capabilities', 'actions', '__expand__'] as const;

const ItemsTab: React.FC = () => {
  const { t, i18n } = useTranslation();
  const moneyVisibility = useInventoryMoneyVisibility();
  const { message, modal } = App.useApp();
  const { handleError } = useSmartErrorHandling();
  const { remember, getParams } = useLastListParams();

  const [reloadKey, setReloadKey] = useState(0);
  const [movementItem, setMovementItem] = useState<InventoryItem | null>(null);
  const [formOpen, setFormOpen] = useState(false);
  const [editingItem, setEditingItem] = useState<InventoryItem | null>(null);

  const loadEditor = useEditorLoad();
  const openEditor = useCallback(
    (itemId: number) =>
      loadEditor(
        () => getItem(itemId, rawText()),
        (full) => {
          setEditingItem(full);
          setFormOpen(true);
        }
      ),
    [loadEditor]
  );

  useConsumeSearchParam('item', (value) => openEditor(Number(value)));

  // Category name lookup for the category column: the items list carries only
  // categoryId. Cached by react-query, so the category select in the form and
  // this table read the same catalogue and one invalidation refreshes both.
  const { globalInventoryCategories, getCategoryName } = useGlobalInventoryCategories();

  // Keep the full DTOs from the last fetch so the record-movement modal (and the
  // edit form) receive a complete `InventoryItem`, not just the display row.
  const itemsByIdRef = useRef<Map<number, InventoryItem>>(new Map());

  const fetchItems = useCallback(
    async (params: { limit: number; offset: number; query?: string; sort?: string; [key: string]: unknown }) => {
      const { limit, offset, query, sort } = params;
      const response = await listItems(
        { offset, limit, ...(query ? { query } : {}), ...(sort ? { sort } : {}) },
        remember
      );
      const map = new Map<number, InventoryItem>();
      response.data.forEach((item) => map.set(item.itemId, item));
      itemsByIdRef.current = map;
      return {
        data: response.data,
        total: response.total || response.data.length,
      };
    },
    [remember]
  );

  const openEditForm = useCallback((row: InventoryItemRow) => openEditor(row.itemId), [openEditor]);

  const handlers = useMemo<InventoryTableHandlers>(
    () => ({
      // The row is display-cased; the form loads the stored item so a save cannot rewrite it.
      onEdit: (row) => openEditor(row.itemId),
      onRecordMovement: (row) => {
        const item = itemsByIdRef.current.get(row.itemId);
        if (item) setMovementItem(item);
      },
      onDelete: (row) => {
        modal.confirm({
          title: t('inventory-item-delete-confirm-title'),
          content: t('inventory-item-delete-confirm-content', { name: row.name }),
          okText: t('common-delete'),
          okButtonProps: { danger: true },
          cancelText: t('common-cancel'),
          onOk: async () => {
            try {
              await deleteItem(row.itemId);
              message.success(t('inventory-item-delete-success'));
              setReloadKey((k) => k + 1);
            } catch (e) {
              handleError(e as Error, { customMessage: t('inventory-item-delete-failed') });
            }
          },
        });
      },
    }),
    [modal, message, handleError, t, openEditor]
  );

  const tableConfig = useMemo(
    () => ({
      tableId: 'inventory-items',
      initialState: DEFAULT_LIST_INITIAL_STATE,
      storage: DEFAULT_LIST_STORAGE,
      urlParamConfig: { external: { searchTerm: 'search' } },
      fetchData: fetchItems,
      transformData: (item: InventoryItem): InventoryItemRow => ({
        id: item.itemId,
        itemId: item.itemId,
        name: item.name,
        categoryId: item.categoryId,
        categoryName: getCategoryName(item.categoryId) ?? null,
        unit: item.unit,
        // These carry schema defaults server-side, so they are optional on the
        // wire type; fall back to the same defaults the spec declares.
        isConsumable: item.isConsumable ?? false,
        isSellable: item.isSellable ?? false,
        isRentable: item.isRentable ?? false,
        onHand: item.onHand ?? 0,
        lowStock: item.lowStock ?? false,
        createdAt: item.createdAt,
      }),
      rowKey: 'id' as const,
      // Double-click anywhere on the row opens the same edit drawer as the pencil.
      // The table skips this for clicks that land on a button, so the action
      // icons and the expand toggle keep working.
      onRowDoubleClick: openEditForm,
      columns: getInventoryItemsTableColumns(t, handlers),
      searchPlaceholder: t('inventory-search-placeholder'),
      searchConfig: {
        minSearchLength: 3,
        debounceMs: 300,
      },
      addButton: {
        text: t('inventory-add-item'),
        onClick: () => {
          setEditingItem(null);
          setFormOpen(true);
        },
      },
      expandable: {
        expandColumnWidth: 35,
        expandColumnSticky: 'left' as const,
        expandedRowRender: (row: InventoryItemRow) => <InventoryMovementsSubTable itemId={row.itemId} />,
      },
    }),
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [t, i18n.language, fetchItems, handlers, getCategoryName, openEditForm]
  );

  const handleFormSubmit = async (data: InventoryItem) => {
    try {
      const body = toWritableItem(data, moneyVisibility);
      if (editingItem) {
        await updateItem(editingItem.itemId, body);
        message.success(t('inventory-item-update-success'));
      } else {
        await createItem(body);
        message.success(t('inventory-item-create-success'));
      }
      setFormOpen(false);
      setEditingItem(null);
      setReloadKey((k) => k + 1);
    } catch (e) {
      handleError(e as Error, {
        customMessage: editingItem ? t('inventory-item-update-failed') : t('inventory-item-create-failed'),
      });
    }
  };

  return (
    <InventoryReloadContext.Provider value={reloadKey}>
      <AppDataTable
        appearance={LIST_TABLE_APPEARANCE}
        config={tableConfig}
        export={{ list: 'inventory_items', getParams, notExported: [...NOT_EXPORTED_INVENTORY_ITEMS] }}
        // A refresh token, not a `key`: the categories arrive after the first
        // fetch, and remounting to pick them up would throw away the search term
        // and the page the user is on. The token identifies the data snapshot the
        // rows were built from — item writes plus the category catalogue.
        refreshToken={`${reloadKey}-${globalInventoryCategories.length}`}
        rightContent={
          // A real link with target=_blank: opens the settings page in its own
          // browser tab so the items list keeps its search/paging state, and
          // cmd/middle-click behave as users expect.
          <Button href={`/${APP_PREFIX}/${INVENTORY_CATEGORY_PAGE}`} target="_blank" rel="noopener noreferrer">
            {t('inventory-manage-categories')}
          </Button>
        }
      />

      {/* Side drawer rather than a centred modal: the form is tall and this keeps
          the items list visible behind it while adding or editing. */}
      <Drawer
        open={formOpen}
        title={editingItem ? t('inventory-item-edit-title') : t('inventory-item-create-title')}
        onClose={() => {
          setFormOpen(false);
          setEditingItem(null);
        }}
        placement="right"
        width={760}
        destroyOnHidden
      >
        {formOpen && (
          <InventoryForm
            key={editingItem?.itemId ?? 'new'}
            data={editingItem ?? undefined}
            submitLabel={editingItem ? t('common-save') : t('common-create')}
            onSubmit={handleFormSubmit}
            extraClasses=""
          />
        )}
      </Drawer>

      <RecordMovementModal
        open={movementItem !== null}
        item={movementItem}
        onClose={() => setMovementItem(null)}
        onSaved={() => setReloadKey((k) => k + 1)}
      />
    </InventoryReloadContext.Provider>
  );
};

export default ItemsTab;
